/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createNodeRuleSet, createVirtualViaNode, matchLayerIdentifier, PcbNodeTypes, } from "@buildwithflux/models";
import { get, maxBy, pickBy, set } from "lodash";
import RBush from "rbush";
import { Clipper } from "../../../../../clipper";
import { box2 } from "../../../../../common/geometry/box2";
import { getPcbNodeCutOut, triangulateShape } from "../../../../../fills/FillHelpers";
import { PcbNodeHelper } from "../../../../../nodes";
import { createCircleOrOblongShape } from "../../../../../shapes";
import { FootPrintPadHoleType } from "../../../../rules";
import { ISLAND_VIA_NAME_PREFIX } from "../../../constants";
function getLayoutStackupAsArray(layout) {
    const stackUp = Object.values(layout.bakedRules.stackup ?? {});
    return stackUp;
}
function getStitchViaUid(net, uidCounter) {
    return `${net.uid}.via.stitch.${uidCounter}`;
}
function isStitchViaUid(uid) {
    return uid.includes(`.via.stitch.`);
}
function getIslandsElectricallyConnectedToNet(context, shapeIslands, net, keepAtLeastOne = false) {
    // Create an rtree index of triangulated/partitioned island shapes, for quick intersection
    // tests later. This speeds things up considerably, because island shapes are very
    // complex and intersecting them with many pcb nodes is slow.
    const shapeIslandsOnNet = new Set();
    const allPartitions = [];
    for (const island of shapeIslands) {
        const partitions = triangulateShape(island.shape);
        allPartitions.push(...partitions.map((partition) => {
            const bounds = box2.fromPoints(partition) ?? box2.empty();
            return {
                ...box2.toRBush(bounds),
                island,
                partition,
            };
        }));
    }
    const shapeIslandIndex = new RBush();
    shapeIslandIndex.load(allPartitions);
    // Iterate islands in search for any that are electrically connected to the net
    for (const pcbNode of Object.values(context.pcbNodes)) {
        if (pcbNode.type !== PcbNodeTypes.via &&
            pcbNode.type !== PcbNodeTypes.pad &&
            pcbNode.type !== PcbNodeTypes.routeSegment) {
            // The node is a type that cannot be electrically connected to the net
            continue;
        }
        // HACK: First check for bakedRules because of the issue where a node is not baked (maybe orphaned?)
        // node is not baked
        // TODO: fix with FLUX-4396
        if (!pcbNode.bakedRules || pcbNode.bakedRules.effectiveNetId !== net.uid) {
            // Skip this node if it's not associated with the net
            continue;
        }
        if (pcbNode.type === PcbNodeTypes.via && isStitchViaUid(pcbNode.uid)) {
            // TODO: consider using PcbNodeHelper.isVirtualNode(..) ?
            // Island connecting vias are transient between fill updates. If we encounter an island connecting via
            // in the PCB nodes map, don't consider it a source of connectivity between fill geometry. Why? Because
            // the island connecting via we're currently on, might not (or should not) actually remain after this
            // update
            continue;
        }
        const isTerminal = net.terminals?.[pcbNode.uid];
        const isSubModuleTerminal = net.submoduleTerminals?.[pcbNode.uid];
        if (!isTerminal && !isSubModuleTerminal) {
            // The node is not electrically connected as a terminal to the net, either as a direct terminal
            // or as a terminal by submodule. We therefore early exit
            continue;
        }
        // Special case for plated through hole pads, connectivity possible when a common connected layer is found
        if (pcbNode.type === PcbNodeTypes.pad &&
            pcbNode.bakedRules.hole?.holeType === FootPrintPadHoleType.platedThroughHole) {
            if (!PcbNodeHelper.isNodeOnNetConnectedLayers(pcbNode, net)) {
                continue;
            }
        }
        const nodeCutout = getPcbNodeCutOut(pcbNode, 4, context, true);
        // Filter node cutout with zero area to prevent runtime exception from attempt to compute intersection with
        // this shape
        if (!nodeCutout || nodeCutout.totalArea() === 0) {
            continue;
        }
        const bounds = nodeCutout.normalizedBounds();
        const partitionCandidates = shapeIslandIndex.search(box2.toRBush(bounds));
        const islandCandidates = new Map();
        for (const candidate of partitionCandidates) {
            if (!islandCandidates.has(candidate.island)) {
                islandCandidates.set(candidate.island, []);
            }
            islandCandidates.get(candidate.island)?.push(candidate.partition);
        }
        for (const [island, partitions] of islandCandidates) {
            // Special case for surface mount pads, connectivity only possible with shape when both share common layer
            if (pcbNode.type === PcbNodeTypes.pad &&
                pcbNode.bakedRules.hole?.holeType === FootPrintPadHoleType.SurfaceMountDevice &&
                !matchLayerIdentifier(island.layer, pcbNode.bakedRules.layer) &&
                !PcbNodeHelper.isNodeOnNetConnectedLayers(pcbNode, net)) {
                continue;
            }
            // Special case for route segments, connectivity only possible with shape when both share common layer
            if (pcbNode.type === PcbNodeTypes.routeSegment &&
                !matchLayerIdentifier(island.layer, pcbNode.bakedRules.layer)) {
                continue;
            }
            // Filter node cutout with zero area to prevent runtime exception from attempt to
            // compute intersection with this shape
            if (island.shape.totalArea() === 0) {
                continue;
            }
            // To test intersection here, we don't need to intersect the entire island shape (which
            // typically has thousands of vertices) because we already know which
            // triangles/partitions are hit candidates, so we only need to test those few simple
            // shapes. This is hundreds of times faster on complex projects.
            //
            // Note: it is important that we test actual poly-poly intersection here, and not just
            // low-fi approximations like "bounds intersect" or "point in shape". So we do a full
            // clipper intersect on each partition below (which is functionally equivalent to a full
            // shape intersect, but faster as mentioned above).
            //
            // Some history on an earlier bug from when we didn't do a full shape intersect:
            // https://www.figma.com/file/Rvsmw4Lc1qpPcs6HfnNoHF/Untitled?type=whiteboard&node-id=0-3&t=i5Jp5pyMdHpZTZIU-4
            // [workplace-post/1174847483859337](/docs/workplace/1174847483859337.md)
            let somePartsIntersect = false;
            for (const p of partitions) {
                try {
                    if (Clipper.shape([p]).intersect(nodeCutout).totalArea() > 0) {
                        somePartsIntersect = true;
                        break;
                    }
                }
                catch (err) {
                    // `earcut` triangles are not always perfectly-formed according to clipper (sometimes
                    // the triangles can be extremely thin and clipper sees these as lines, and returns
                    // "invalid paths"). Treat these as zero-area and therefore consider them to have
                    // zero intersection area; if there are real intersections with the total shape, there
                    // should be bigger triangles immediately adjacent that return better results.
                }
            }
            // Since the triangulation is a complete representation of the shape (and since earcut
            // accounts for holes), there's a 1-to-1 correspondence between "some parts intersect"
            // and "the original shape intersects"
            if (somePartsIntersect) {
                shapeIslandsOnNet.add(island);
            }
        }
    }
    // Special case: for polygons we want to keep at least one island
    // This is because otherwise it's possible for the user to draw a polygon and no fill will happen until something is connected to it.
    // This feels a bit magic, it's a better user experience to have polygons be filled, even if it causes floating copper.
    if (keepAtLeastOne && shapeIslandsOnNet.size === 0) {
        const biggestShape = maxBy(shapeIslands, (shapeIsland) => shapeIsland.shape.totalArea());
        if (biggestShape) {
            shapeIslandsOnNet.add(biggestShape);
        }
    }
    return shapeIslandsOnNet;
}
/** Return any overlapping sub-shapes from the provided fill shapes that do not have an electrical
 * connection to other layers through a via or through-hole pad.
 */
function unconnectedIntersectionShapes(context, fillShapesByLayer, obstacleShapes, pcbNodes, net) {
    // TODO it would be nice if we could get the intersection of more than two shapes at once
    // with clipper, but it doesn't work as you'd expect. Fortunately this is not (currently)
    // a huge bottleneck. But some point it may be!
    const intersection = Object.values(fillShapesByLayer).reduce((intersection, layerShape) => (intersection ? intersection.intersect(layerShape) : layerShape), null);
    if (!intersection) {
        return [];
    }
    // TODO it's very unclear to me why we subtract obstacleShapes here, when the fill shape should
    // already have all the keepout shapes cut out from it earlier by CopperFillHandler. It looks
    // like there may be confusion between `pcbNodeHelpers.stitchingShouldAvoidNode` (which produces
    // `obstacleShapes`) and `FillHelpers.fillGetKeepoutMode` (which CopperFillHandler uses to cut
    // out the keepout regions)
    const layerIntersection = intersection.subtract(Object.entries(obstacleShapes)
        .filter(([uid]) => {
        const pcbNode = pcbNodes[uid];
        if (pcbNode?.type === PcbNodeTypes.via || pcbNode?.type === PcbNodeTypes.smartVia) {
            if (pcbNode.bakedRules.effectiveNetId === net.uid) {
                return false;
            }
        }
        return true;
    })
        .map(([, [shape]]) => shape));
    // Collect all children of this net, which can provide connectivity between fill shapes of the net
    const connectingNodeShapes = [];
    for (const childUid of net.childUids) {
        const childNode = pcbNodes[childUid];
        if (childNode && isNetChildConnectorBetweenFillLayers(childNode, net)) {
            const cutout = getPcbNodeCutOut(childNode, 4, context, true);
            if (cutout) {
                connectingNodeShapes.push(cutout);
            }
        }
    }
    // Filter intersection shapes by those that do not have any inter-layer connectivity
    // TODO I'm surprised this is as fast as it is! It only takes up ~30ms of a
    // ~30000ms generate-fills operation (in a large project), even though it seems to
    // be doing something I would've thought was similar time-complexity to the
    // worst offender, layerIntersections.map(shape => shape.difference(obstacles)) in
    // FillVirtualNodeGenerator:generate():597. Both loops do a clipper operation for
    // each individual intersection shape. Are the connectingNodeShapes here just that
    // much simpler? Is there a bug that causes most of them to get skipped or something?
    // Or is .intersect fast because it can do a bbox test for early exit in most cases?
    const unconnectedShapes = [];
    for (const intersectionShape of layerIntersection.separateShapes()) {
        const intersectionShapeIsEmpty = intersectionShape.isEmpty();
        let hasConnection = false;
        for (const connectingNodeShape of connectingNodeShapes) {
            if (!connectingNodeShape.isEmpty() &&
                !intersectionShapeIsEmpty &&
                connectingNodeShape.intersect(intersectionShape).totalArea() > 0) {
                hasConnection = true;
                break;
            }
        }
        if (!hasConnection) {
            unconnectedShapes.push(intersectionShape);
        }
    }
    return unconnectedShapes;
}
function getShapeIslands(layout, fillShapesWithCutoutByLayerId) {
    const repository = [];
    const stackUp = getLayoutStackupAsArray(layout);
    let id = 0;
    for (const [layerId, shape] of Object.entries(fillShapesWithCutoutByLayerId)) {
        const layer = stackUp.find((layer) => layer.uid === layerId);
        if (!layer) {
            throw new Error(`Failed to find layer ${layerId} when building fill island repository`);
        }
        for (const shapePart of shape.separateShapes()) {
            repository.push({
                shape: shapePart,
                layer,
                id: id++,
            });
        }
    }
    return repository;
}
/**
 * Is the node able to connect fill for the net, across multiple layers?
 *
 * Returns true for non-virtual vias or pads that belong to the given net, can
 * connect multiple layers, and share at least one connected layer with the net.
 */
function isNetChildConnectorBetweenFillLayers(node, net) {
    // For net children only..
    if (node.parentUid !== net.uid) {
        return false;
    }
    // Disabled node does not exist in physical PCB and should therefore not be a source of island connection between layers
    // HACK: First check for bakedRules because of the issue where a node is not baked (maybe orphaned?)
    if (node.bakedRules && !node.bakedRules.active) {
        return false;
    }
    // Early cull to skip any PCB nodes that cant connect islands between layers
    if (node.type !== PcbNodeTypes.via && node.type !== PcbNodeTypes.pad) {
        return false;
    }
    /*
    Cannot assume effectiveNetId is set. Ex, an STD pad created by user, manually under net node, wont have effectiveNetId set..
    //
    // TODO ^ this is an inconsistency we should define: do pads belong to nets by virtue of a) belonging to a terminal
    // connected to that net in schematic, or b) being children of the net, or c) both?
    // Currently (a) is definitely true of pads (deeply core to net functionality), and (b) is true of vias and traces,
    // but not necessarily pads.
    // Either way, we should not be treating pads differently here than we do eg in fills and elsewhere. If they don't
    // have a effectiveNetId, then they don't belong to a net! And if they should belong to this net, then we should give
    // them a effectiveNetId!!
    //
    // In fact, maybe a better way to find nodes that belong to a net in pcb is to filter all nodes by effectiveNetId, rather
    // than looking at net children/descendents?
    //
    // What user scenarios do we have where the below line would make a difference? It'd be helpful to get examples.
    if (node.bakedRules.effectiveNetId !== net.uid) {
        return false;
    }
    */
    // Another early cull to skip Pads that cannot connect islands between layers
    if (node.type === PcbNodeTypes.pad && node.bakedRules.hole?.holeType === FootPrintPadHoleType.SurfaceMountDevice) {
        return false;
    }
    if (PcbNodeHelper.isVirtualNode(node)) {
        return false;
    }
    // Node and net have to share common layers via Connected Layers rule to be source of island connection between layers
    if (!PcbNodeHelper.isNodeOnNetConnectedLayers(node, net)) {
        return false;
    }
    return true;
}
function getPotentiallyConnectingNetChildren(pcbNodes, net) {
    return Object.values(pcbNodes).filter((node) => isNetChildConnectorBetweenFillLayers(node, net));
}
/** For the given fill islands that have cross-layer overlap but don't have connections (yet), return the
 * positions for island vias to connect them. If any given overlap area already has a grid-aligned stitching
 * via, then include the position of that instead.
 *
 * The returned value is an array of grid- and island-via positions.
 */
export function getIslandViaPositionsFromIntersection(stitchViasGridAligned, manualViaPositions, unconnectedLayerIntersections, viaSize, minTraceWidth) {
    const viaPositions = [];
    const viaBoxSize = viaSize + minTraceWidth * 2;
    // TODO maybe play with the entries-per-node param of RBush? (trades off insertion vs. search speed;
    // we want high search speed here, very likely) https://github.com/mourner/rbush?tab=readme-ov-file#creating-a-tree
    // TODO for 2-4x faster static lookup (no insertion after creation), we should use https://github.com/mourner/flatbush
    // TODO for 5-8x faster static lookup, we should instead search for points (not boxes) within a slightly larger box
    // (intersectionShapePart.bounds() + viaBoxSize) so we can use https://github.com/mourner/kdbush
    const stitchViaIndex = new RBush();
    stitchViaIndex.load(stitchViasGridAligned.map((v) => ({
        ...box2
            .chain(box2.fromPoints([v.pos]))
            .expandByScalar(viaBoxSize / 2)
            .toRBush(),
        via: v,
    })));
    const getStitchViasOnShape = (intersectionShapePart) => {
        // If a user created via is contained in the intersection shape we'll use that as the source
        // of connectivity between layers of copper fill
        const bounds = intersectionShapePart.normalizedBounds();
        const candidateVias = stitchViaIndex.search(box2.toRBush(bounds));
        const triangles = triangulateShape(intersectionShapePart);
        const partitionIndex = new RBush();
        partitionIndex.load(triangles.map((t) => ({
            ...box2.toRBush(box2.fromPoints(t) ?? box2.empty()),
            partition: t,
        })));
        const stitchViasOnIntersectionShape = candidateVias
            .map((v) => v.via)
            .filter((via) => {
            const diameter = viaSize + minTraceWidth;
            const sides = 4; // Keep a coarse, simple cutout shape for performance
            const copperShape = createCircleOrOblongShape({ x: diameter, y: diameter }, { x: via.pos.x, y: via.pos.y }, undefined, sides).getPoints();
            const copperShapeAsClipper = Clipper.shape([copperShape]);
            const copperBounds = box2.fromPoints(copperShape);
            const candidateTriangles = copperBounds ? partitionIndex.search(box2.toRBush(copperBounds)) : [];
            if (!candidateTriangles.length) {
                // Early exit if there's no match at all, to save calling `pointInShape()` below needlessly
                return false;
            }
            const intersects = candidateTriangles.some((tri) => {
                const shape = Clipper.shape([tri.partition]);
                const shapeArea = shape.totalArea();
                if (shapeArea === 0)
                    return false; // or else it might crash clipper
                return shape.intersect(copperShapeAsClipper).totalArea() > 0;
            });
            // The `pointInShape` test below is important in addition to the intersect test, at
            // least in order to preserve previous behaviour -- for example in the project
            // bryan/coffee-waker-main-hq-w-module-v3p2p3-with-polygons where we noticed that
            // extra vias were being created that intersected too much with a polygon on another
            // net, taking a too-large keepout bite out of the polygon.
            //
            // That said, the fact that the pointInShape here causes the avoidance of polygons
            // on a different net is really a side-effect (likely unintended, even if wanted?)
            // of the combination of a) requiring vias to intersect all fill layers, and b)
            // requiring via centers to intersect the fills. If we remove either of those
            // requirements, then vias will no longer mostly-avoid polygons. Feels fragile and
            // not very intentional!
            //
            // Any of those three features may be wanted and intentional independently. But we
            // should be explicit about them each if so, and not have them as surprise
            // side-effects of each other.
            //
            // TODO it's also prob bad how many different places we check if a via is in the
            // fill! We check in gridAlignedVias() before running this function that via centers
            // intersect at least two fill layers. Then we check here that the vias *and* their
            // centers intersect *all* the fill layers. Then we check later in `getViaPositions`
            // that their centers intersect at least one fill shape. And perhaps elsewhere. I'm
            // sure some of those checks are redundant, and the slight differences between them
            // (centers in some places, intersection elsewhere) are confusing and uncommented
            // and I'm not convinced they are fully intentional.
            return intersects && intersectionShapePart.containsPoint(via.pos);
        });
        return stitchViasOnIntersectionShape;
    };
    for (const intersectionShapePart of unconnectedLayerIntersections) {
        // If a user created via is contained in the intersection shape we'll use that as the source
        // of connectivity between layers of copper fill
        const stitchViasOnIntersectionShape = getStitchViasOnShape(intersectionShapePart);
        if (stitchViasOnIntersectionShape.length > 0) {
            viaPositions.push(...stitchViasOnIntersectionShape);
            // Since we have at least one stitch via that'll connect layers related to this intersection shape, we can
            // skip calculation of coordinates for a regular island via that'd otherwise be needed to connect copper fill
            // across layers of this intersection shape
            continue;
        }
        const intersectionShapePartContainsManualVia = manualViaPositions.some((viaManualPosition) => 
        // Note: keeping viaManualPositions as vector2 is important to ensure correct scaling of coordinate in point-in-shape test
        intersectionShapePart.containsPoint(viaManualPosition));
        if (intersectionShapePartContainsManualVia) {
            // The intersection shape contains a user created via, so we skip calculation of coordinates for
            // a regular island via - look at the 'islandAndStitchVias' variable, that contains a mix of
            // positions from user via positions and island via positions, for island via creation
            continue;
        }
        // Guard against polylabel crashing
        if (intersectionShapePart.isEmpty()) {
            continue;
        }
        const bounds = intersectionShapePart.normalizedBounds();
        // Early exit if the bounds or area wouldn't even allow a via to fit, before running the more
        // expensive polylabel.
        const size = box2.size(bounds);
        const spaceNeeded = viaSize + minTraceWidth * 2;
        if (size.x < spaceNeeded || size.y < spaceNeeded || intersectionShapePart.totalArea() < spaceNeeded ** 2) {
            continue;
        }
        // In Feb 2025 we forked from the polylabel library for performance
        //
        // Note: a high-ish precision value (in the hundreds) here is important, because this polygon
        // is in Clipper int space, i.e. meters x 1M, i.e. microns, and micron-level accuracy is
        // *extremely* slow here and unnecessary.
        //
        // Note that the resulting position is tested further below to ensure it is still on the polygon,
        // so we don't use solutions that didn't converge well.
        //
        // @bryhoyt found a value of 400 to be good in all scenarios I could think of, with many
        // large and small polygons, right down to polygons as small as our min feature size
        // (default min trace width). A value of 200 gives this a good 2x margin of error, and is
        // still fast on our large test projects (or at least hugely faster than the previous value
        // of 1.0)
        //
        // For backwards-compatibility here, we still test a high-precision value of 5 if the low-precision
        // value fails. This isn't necessary for valid results, but it causes us to remove some islands
        // that weren't removed in previous versions, and we want to preserve user project behaviour.
        // It sadly does cost us some performance, but not as much as it did previously, because
        // 1) the early-exit checks above, 2) the first low-precision try catches many hits, and
        // 3) we've loosened the high-precision value to 5 (12 was enough to preserve all known
        // instances).
        const POLYLABEL_PRECISION_TEST_POINTS = [200, 5];
        for (const precision of POLYLABEL_PRECISION_TEST_POINTS) {
            const { pos, distance } = intersectionShapePart.poleOfInaccesiblity(precision);
            // TODO: ensure no self-intersection between this via and previous
            // via positions
            // Only allow via position to be included if the via can actually fit at the coordinate of
            // pole of inaccessibility (i.e. in case polylabel didn't converge on a fully-intersecting
            // solution)
            if (distance > spaceNeeded / 2) {
                viaPositions.push({
                    type: "island",
                    pos,
                });
                break;
            }
        }
    }
    return viaPositions;
}
function getIslandClusters(context, shapeIslands, potentiallyConnectingNetChildren, stitchVias, stitchViasSize, // TODO: integrate
manualViaPositions, singleLayer) {
    // Special case: for single layer fills, we can't stitch between layers, return the biggest island
    if (singleLayer) {
        const result = [];
        const biggestShape = maxBy(shapeIslands, (shapeIsland) => shapeIsland.shape.totalArea());
        if (biggestShape) {
            result.push(new Set([biggestShape]));
        }
        return result;
    }
    const potentiallyConnectingNetChildrenShapes = potentiallyConnectingNetChildren
        .map((pcbNode) => getPcbNodeCutOut(pcbNode, 4, context, true))
        .filter((pcbNode) => !!pcbNode);
    // Allow island clusters to be created from both island vias, and manual user placed vias
    const islandAndStitchVias = [...stitchVias.map((stitchVia) => stitchVia.pos), ...manualViaPositions];
    const islands = [
        ...islandAndStitchVias.map((islandViaPosition) => {
            const islandCluster = new Set();
            for (const island of shapeIslands.values()) {
                // TODO: replace with shape intersection area
                // TODO and when we do the above, be sure to use an RBush as elsewhere to filter candidates quickly
                if (island.shape.containsPoint(islandViaPosition)) {
                    islandCluster.add(island);
                }
            }
            return islandCluster;
        }),
        ...potentiallyConnectingNetChildrenShapes.map((shape) => {
            const islandCluster = new Set();
            for (const island of shapeIslands.values()) {
                if (!shape.isEmpty() && island.shape.intersect(shape).totalArea() > 0) {
                    islandCluster.add(island);
                }
            }
            return islandCluster;
        }),
    ];
    // The following algorithm looks at an array of sets and, for any set that shares elements with another, those set
    // 'pairs' are merged. The sets that are merged, are removed from the output, and only merged sets remain. This is
    // the mechanism for detecting island connectivity
    for (let i = 0; i < islands.length; i++) {
        let clearI = false;
        const islandI = islands[i];
        for (let j = i + 1; j < islands.length; j++) {
            const islandJ = islands[j];
            // If one or more shapes from islandI exists in islandJ, then these islands are considered to be connected
            const islandsConnect = Array.from(islandI).some((shape) => islandJ.has(shape));
            if (islandsConnect) {
                // The contents of islandI are merged into islandJ, and islandI is then marked for removal
                for (const shape of islandI) {
                    islandJ.add(shape);
                }
                clearI = true;
            }
        }
        if (clearI) {
            // Clearing islandI is how we mark islandI for removal. The final step of this algorithm is then only a matter
            // of filtering non-empty island sets
            islandI.clear();
        }
    }
    const islandClusters = islands.filter((island) => island.size > 0);
    return islandClusters;
}
function mergeIslandsToFills(shapeIslands) {
    const fillByLayer = {};
    for (const shapeIsland of shapeIslands) {
        for (const islandPart of shapeIsland) {
            // This is nessisary to ensure the polygon
            const polygons = get(fillByLayer, [islandPart.layer.uid]) ?? [];
            polygons.push(islandPart.shape);
            set(fillByLayer, [islandPart.layer.uid], polygons);
        }
    }
    // Merge all shapes for each layer
    return Object.fromEntries(Object.entries(fillByLayer).map(([layerId, shapes]) => {
        return [layerId, Clipper.shape([]).union(shapes)];
    }));
}
function createIslandVias(fill, node, stitchVias, _layersToStitch) {
    const addVias = [];
    const connectedLayers = new Set(node.bakedRules.connectedLayers);
    const allowAddIslandVia = connectedLayers.has("All") || connectedLayers.size > 1;
    // Only allow island vias to be added if the net is connected to multiple layers - this condition prevents
    // a single redundant island via being inserted on the Fill:
    // https://www.loom.com/share/898b86524ee649c88baa0a30dcd32eae?sid=f8501e22-42c3-4d41-8830-e6a3e97cc556
    if (!allowAddIslandVia) {
        return addVias;
    }
    let islandViaCounter = 0;
    let uidCounter = 0;
    for (const stitchVia of stitchVias) {
        // Since the fill/polygon node can be translated/rotated
        const viaPositionRelative = PcbNodeHelper.layoutPositionToNodeSpace(stitchVia.pos, fill);
        const fields = {
            uid: getStitchViaUid(node, uidCounter),
            parentUid: fill.uid,
            pcbNodeRuleSet: createNodeRuleSet({
                position: `${viaPositionRelative.x * 1000}mm ${viaPositionRelative.y * 1000}mm`,
                // Disabled for now, as it could create unconnected vias
                // Example: a net with both a polygon and a full-board fill will have double stitching vias,
                // and they won't have the correct connected layers value
                // connectedLayers: layersToStitch.join(","),
            }),
        };
        if (stitchVia.type === "grid-aligned") {
            addVias.push(createVirtualViaNode(`Via-${stitchVia.col}-${stitchVia.row}`, fill.uid, node.uid, "fill", fields));
        }
        else {
            addVias.push(createVirtualViaNode(`${ISLAND_VIA_NAME_PREFIX}${islandViaCounter}`, fill.uid, node.uid, "fill", fields));
            islandViaCounter++;
        }
        uidCounter++;
    }
    return addVias;
}
function getViaPositions(shapeIslandsOnNet, islandConnections) {
    const stitchViaSet = new Set(islandConnections);
    const stitchViasFiltered = [];
    for (const shapeIslands of shapeIslandsOnNet) {
        for (const shapeIsland of shapeIslands) {
            const containedStitchVias = new Set();
            // TODO use rbush (or kdbush) to eliminate O(n^2) behaviour here
            for (const viaPosition of stitchViaSet) {
                if (shapeIsland.shape.containsPoint(viaPosition.pos)) {
                    containedStitchVias.add(viaPosition);
                }
            }
            for (const containedStitchVia of containedStitchVias) {
                stitchViaSet.delete(containedStitchVia);
                stitchViasFiltered.push(containedStitchVia);
            }
            if (stitchViaSet.size === 0) {
                break;
            }
        }
        if (stitchViaSet.size === 0) {
            break;
        }
    }
    return stitchViasFiltered;
}
/**
 * This function accumulates via cutouts, for each island connection, into the cumulative island cutout shape. The updated
 * cumulative island cutout is returned from this function.
 * The purpose of this function is to build a 'geometry of obstacles', used to prevent subsequently added island connecting
 * vias from overlapping previously inserted island connecting vias. Note that island connecting vias are inserted based on
 * a geometry defining empty regions of space. The cumulative geometry that this function generates is 'subtracted from empty
 * space', which basically informs the via insertion algorithm to where island connecting vias (from other nets) have been placed.
 */
function getIslandViaShapes(islandViaPositions, islandViaSize) {
    const islandViaCutouts = islandViaPositions.map((islandViaPosition) => {
        const diameter = islandViaSize;
        const sides = 4;
        const copperShape = createCircleOrOblongShape({ x: diameter, y: diameter }, islandViaPosition.pos, undefined, sides).getPoints();
        return Clipper.shape([copperShape]);
    });
    return islandViaCutouts;
}
const filterUserCreatedNodeOnNetConnectedLayer = (net, updatedNodes) => {
    return pickBy(updatedNodes, (node) => {
        switch (node.type) {
            case PcbNodeTypes.via: {
                if (node.parentUid === net.uid && // Filtering by children on net, not baked.effectiveNet
                    PcbNodeHelper.isNodeOnNetConnectedLayers(node, net)) {
                    return false;
                }
                // TODO: improve this - this filters out vias that are descendants of a smart via, from
                // the nodes map used to generate obstacles from
                if (node.parentUid) {
                    const smartVia = updatedNodes[node.parentUid];
                    if (smartVia?.type === PcbNodeTypes.smartVia && smartVia.bakedRules.effectiveNetId === net.uid) {
                        return false;
                    }
                    // eslint-disable-next-line etc/no-commented-out-code
                    /*
                    if (smartVia?.type === PcbNodeTypes.smartVia ) {
                        const smartViaNet = updatedNodes[smartVia.parentUid];
                        if (smartViaNet?.type === PcbNodeTypes.net && smartViaNet.bakedRules.effectiveNetId === net.uid) {
                            return false;
                        }
                    }
                    */
                }
                break;
            }
            case PcbNodeTypes.pad: {
                if (node.parentUid === net.uid && // Filtering by children on net, not baked.effectiveNet
                    node.bakedRules.hole?.holeType === FootPrintPadHoleType.platedThroughHole &&
                    PcbNodeHelper.isNodeOnNetConnectedLayers(node, net)) {
                    return false;
                }
                break;
            }
        }
        return true;
    });
};
export const FillIslandRemoval = {
    getViaPositions,
    getIslandViaPositionsFromIntersection,
    getIslandClusters,
    getIslandsElectricallyConnectedToNet,
    getPotentiallyConnectingNetChildren,
    filterUserCreatedNodeOnNetConnectedLayer,
    mergeIslandsToFills,
    getShapeIslands,
    createIslandVias,
    getIslandViaShapes,
    unconnectedIntersectionShapes,
};
//# sourceMappingURL=FillIslandRemoval.js.map